import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import CookieModal from '@starlingbank/cookie-modal';
import { paths } from '@starlingbank/shared';
import { slugify } from '../../utils';
import { copyConstants } from '@starlingbank/shared';

import none from '../../../content/media-library/icons/service-status/status-none.svg';
import minor from '../../../content/media-library/icons/service-status/status-minor.svg';
import major from '../../../content/media-library/icons/service-status/status-major.svg';
import critical from '../../../content/media-library/icons/service-status/status-critical.svg';

const { footer, socials } = paths;

const LinkItems = ({ menuItem, currentPath, className, serviceStatus }) => {
  const handleClick = (footerItem) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'footerItemChanged',
        footerSection: menuItem,
        footerItem
      });
    }
  };

  const statuses = {
    none: { src: none, alt: 'All systems operational.' },
    minor: { src: minor, alt: 'Minor service outage.' },
    major: { src: major, alt: 'Major service outage.' },
    critical: { src: critical, alt: 'Critical service outage.' }
  };

  return (
    <ul>
      {footer[menuItem].paths.map((path) => (
        <li
          key={slugify(path.name)}
          className={`${className}${
            currentPath + '/' === path.href || currentPath === path.href ? ' main-footer__site-map-link--active' : ''
          }`}
        >
          <a
            href={path.href}
            className={path.className ? `${path.className} ` : undefined}
            onClick={() => handleClick(path.name)}
            target={path.external ? '_blank' : undefined}
            rel={path.external ? 'noopener noreferrer' : undefined}
          >
            {path.name}
            {path.icon ? (
              <div className="status-link__status">
                <img src={statuses[serviceStatus].src} alt={statuses[serviceStatus].alt} />
              </div>
            ) : (
              ''
            )}
          </a>
        </li>
      ))}
      {menuItem === 'Legal' ? (
        <li className={className}>
          <CookieModal />
        </li>
      ) : null}
    </ul>
  );
};

const SocialLinks = ({ paths, className }) => (
  <div className="main-footer__split">
    <div className="main-footer__split-social_heading">Connect with us</div>
    <ul className="main-footer__links-list">
      {paths.map((path) => (
        <li key={slugify(path.aria)} className={className}>
          <a
            href={path.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Link to Starling Bank ${path.aria}`}
          >
            <span className={`${path.icon} site-footer__social`} />
            {path.type && path.type === 'business' ? <span className="biz-twitter">B</span> : ''}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = ({ currentPath }) => {
  const data = useStaticQuery(graphql`
    {
      file(relativePath: { regex: "/media-library/logos/fscsWhite.png/" }) {
        absolutePath
        childImageSharp {
          gatsbyImageData(width: 128, layout: FIXED, placeholder: NONE)
        }
      }
    }
  `);

  const [serviceStatus, setServiceStatus] = useState('none');

  useEffect(() => {
    if (typeof window.StatusPage === 'undefined') return;
    const sp = new window.StatusPage.page({ page: 'z8sh6nsldxtk' });
    sp.summary({
      success: function (data) {
        setServiceStatus(data.status.indicator);
      }
    });
  }, []);

  return (
    <footer className="main-footer">
      <section className="main-footer__site-map">
        {Object.keys(footer).map((menuItem) => (
          <div key={slugify(menuItem)} className="main-footer-section">
            <div className="main-footer__heading">{menuItem}</div>
            <LinkItems
              menuItem={menuItem}
              currentPath={currentPath}
              className="main-footer__site-map-link"
              serviceStatus={serviceStatus}
            />
          </div>
        ))}
      </section>

      <section className="main-footer__actions">
        <SocialLinks paths={socials.paths} className="main-footer__social-link" />
      </section>

      <section className="main-footer__footnote">
        <a href="https://www.starlingbank.com/legal/account/fscs-protection/">
          <GatsbyImage
            image={data.file.childImageSharp.gatsbyImageData}
            alt="FSCS protected. Select this to go to our Financial Services Compensation Scheme page to find out more."
          />
        </a>
        <div className="main-footer__footnote-text">
          <p className="main-footer__legal-copy">{copyConstants.legalFooterCopy}</p>
          <p className="main-footer__copyright-notice">{copyConstants.copyright}</p>
        </div>
      </section>
    </footer>
  );
};

Footer.propTypes = {
  currentPath: PropTypes.string,
  theme: PropTypes.oneOf(['light'])
};

export default Footer;
